import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Users, CheckCircle, Clock, Info, XCircle, Prohibit } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DisqualifyDialog } from "./DisqualifyDialog";

const fmtTs = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch { return iso; }
};

export const ParticipationDialog = ({ auctionId, canDisqualify = false, onDisqualified }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dqTarget, setDqTarget] = useState(null); // bidder row selected for disqualification

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(`/auctions/${auctionId}/participation`);
      setData(resp);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load participation");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [auctionId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const summary = data?.summary;
  const roster = data?.roster || [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full rounded-none border-black h-11 btn-sharp"
          data-testid="open-participation-dialog"
        >
          <Users size={14} weight="bold" className="mr-2" /> See Participation
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-3xl" data-testid="participation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl tracking-tightest">
            Bidder Participation
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm text-neutral-700 leading-relaxed flex items-start gap-2 border-l-2 border-klein-blue pl-3" data-testid="participation-disclaimer">
          <Info size={14} weight="bold" className="mt-0.5 shrink-0 klein-blue" />
          <span>
            <strong>Prices are still hidden.</strong> This panel shows which invited bidders have submitted any quote — and in which rounds — so you can nudge silent ones. You will never see the price they quoted from here. Use <em>Peek</em> (super-admin only) for that.
          </span>
        </div>

        {loading ? (
          <div className="py-10 text-xs tracking-[0.3em] uppercase text-neutral-500">Loading roster...</div>
        ) : !data ? null : (
          <>
            <div className={`grid ${summary?.disqualified_total > 0 ? "grid-cols-5" : "grid-cols-4"} border border-black/10 mt-2`}>
              <Stat label="Invited" value={summary.invited_total} testid="stat-invited" />
              <Stat label="Have Bid" value={summary.bid_total} testid="stat-bid-total" emphasis />
              <Stat label={`Bid · R${summary.current_round}`} value={summary.bid_this_round} testid="stat-bid-this-round" />
              <Stat label="Silent" value={summary.silent_total} testid="stat-silent" />
              {summary?.disqualified_total > 0 && (
                <Stat label="Disqualified" value={summary.disqualified_total} testid="stat-disqualified" danger />
              )}
            </div>

            <div className="mt-2 max-h-[420px] overflow-y-auto border border-black/10" data-testid="participation-roster">
              {roster.length === 0 ? (
                <div className="p-6 text-sm text-neutral-500 text-center">
                  No bidders invited yet on this auction.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#F8F9FA] border-b border-black/10 sticky top-0">
                    <tr className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Bidder</th>
                      <th className="text-left p-3">Company</th>
                      <th className="text-left p-3">Rounds</th>
                      <th className="text-left p-3">Last activity</th>
                      {canDisqualify && <th className="text-right p-3">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {roster.map((r) => (
                      <tr
                        key={r.email}
                        className={`border-b border-black/5 ${r.is_disqualified ? "bg-red-50/40 opacity-90" : "hover:bg-neutral-50"}`}
                        data-testid={`participation-row-${r.email}`}
                      >
                        <td className="p-3 whitespace-nowrap">
                          {r.is_disqualified ? (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-1 border border-[#B91C1C] text-[#B91C1C] bg-white"
                              title={r.disqualification_reason || ""}
                              data-testid={`dq-badge-${r.email}`}
                            >
                              <Prohibit size={12} weight="bold" />
                              Disqualified
                            </span>
                          ) : r.has_bid ? (
                            <span
                              className={`inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-1 border ${r.bid_this_round ? "border-green-700 text-green-700" : "border-black/40 text-neutral-700"}`}
                              title={r.bid_this_round ? "Bid in the current round" : "Has bid in an earlier round but not the current one"}
                            >
                              <CheckCircle size={12} weight="bold" />
                              {r.bid_this_round ? "Active" : "Bid earlier"}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-1 border border-amber-700 text-amber-700">
                              <Clock size={12} weight="bold" />
                              Silent
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="font-semibold">{r.name || <span className="text-neutral-400 italic">No name yet</span>}</div>
                          <div className="text-[11px] font-mono-plex text-neutral-500">{r.email}</div>
                          {!r.has_account && (
                            <div className="text-[10px] tracking-[0.2em] uppercase text-amber-700 mt-1 font-semibold">
                              Not signed up
                            </div>
                          )}
                          {r.is_disqualified && r.disqualification_reason && (
                            <div className="mt-1.5 text-[11px] text-[#7F1D1D] italic leading-snug border-l-2 border-[#B91C1C] pl-2" data-testid={`dq-reason-${r.email}`}>
                              &ldquo;{r.disqualification_reason}&rdquo;
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-neutral-700">{r.company || "—"}</td>
                        <td className="p-3 font-mono-plex">
                          {r.rounds_participated.length === 0
                            ? "—"
                            : r.rounds_participated.map((rn) => `R${rn}`).join(", ")}
                        </td>
                        <td className="p-3 text-[12px] text-neutral-600 font-mono-plex">
                          {fmtTs(r.last_bid_at)}
                        </td>
                        {canDisqualify && (
                          <td className="p-3 text-right">
                            {!r.is_disqualified ? (
                              <button
                                type="button"
                                onClick={() => setDqTarget(r)}
                                className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-semibold px-2 py-1.5 border border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white transition-colors"
                                data-testid={`disqualify-btn-${r.email}`}
                              >
                                <XCircle size={12} weight="bold" />
                                Disqualify
                              </button>
                            ) : (
                              <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-400 italic">Final</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="text-[11px] text-neutral-500 leading-relaxed">
              <strong>Privacy note:</strong> if only one bidder has bid in the current round, combining this list with the lowest-price card on the auction page can reveal whose bid that is. We surface participation anyway because tracking responsiveness is core to running the auction.
            </div>
          </>
        )}
      </DialogContent>
      {dqTarget && (
        <DisqualifyDialog
          open={!!dqTarget}
          onOpenChange={(v) => { if (!v) setDqTarget(null); }}
          auctionId={auctionId}
          bidder={dqTarget}
          onDone={() => {
            setDqTarget(null);
            load();
            onDisqualified?.();
          }}
        />
      )}
    </Dialog>
  );
};

const Stat = ({ label, value, testid, emphasis, danger }) => (
  <div
    className={`p-4 border-r border-black/10 last:border-r-0 ${emphasis ? "bg-[#F1F5FF]" : ""} ${danger ? "bg-red-50" : ""}`}
    data-testid={testid}
  >
    <div className={`text-[10px] tracking-[0.25em] uppercase mb-1 ${danger ? "text-[#B91C1C]" : "text-neutral-500"}`}>{label}</div>
    <div className={`font-display font-extrabold text-2xl tracking-tightest leading-none ${danger ? "text-[#B91C1C]" : ""}`}>{value}</div>
  </div>
);
